import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, Minus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { capture } from "@/lib/posthog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useResolvedEntitlements } from "@/hooks/use-entitlements";
import type { SystemRole } from "@/lib/rbac";
import {
  guideForRoute,
  type GuideSignal,
  type GuideStep,
  type PageGuide as Guide,
} from "@/lib/page-guides";
import { pageGuideTestIds as ids } from "@/testids/pageGuideTestIds";

/**
 * The page guide — a step-by-step walkthrough, one modal at a time.
 *
 * It replaced a side panel that listed a page's whole surface at once. The
 * panel had to be read and matched to the screen by the reader; this points at
 * one control, waits where they have to act, and advances itself when they do.
 *
 * It never blocks: no scrim, the page stays clickable throughout, and the
 * waiting steps exist precisely so the reader uses the real control rather than
 * a copy of it inside a tour.
 */

// ── What the guide remembers ────────────────────────────────────────────────

/** Fired by the Help menu to reopen the guide at the step it was left on. */
export const OPEN_GUIDE_EVENT = "fastn:open-page-guide";

type Mode = "intro" | "open" | "pill" | "closed";

interface GuideState {
  mode?: Mode;
  step?: number;
  /** Set once the guide has run to the end here, so the toast is not repeated. */
  finished?: boolean;
  /** Set the first time the page is seen, so the opener offers itself once. */
  seen?: boolean;
  /**
   * Which step, by id. `step` below is a position, and a position only means
   * anything while the guide's order holds still: this release reorders
   * Workflows, so anyone mid-guide at the old index 2 would come back to a
   * different step at the new index 2. The id survives a reorder, and an id
   * that no longer exists falls back to the position.
   */
  stepId?: string;
  /**
   * What the page held when the reader began the step they are on, for the
   * signals that compare against it.
   *
   * Kept here because it cannot be re-derived. "increase" and "decrease" ask
   * what changed since the step opened, and on a resumed guide the step
   * opened in another session: sampling again lands on whatever is rendered
   * before the fetch returns, which is nothing. That made Widgets' "pick the
   * apps" baseline 0 and then auto-complete the moment the existing chips
   * arrived, and made "fix the expired one" baseline 0 so no number of
   * reconnections could ever be fewer. Restoring the real count fixes both,
   * which adding a `rebaseOn` to each could not: the only thing to rebase
   * Widgets on is the chips themselves, and that is the reader's own action.
   */
  waitBaseline?: { key: string; count: number };
  /**
   * How many steps the reader has finished by doing rather than reading, kept
   * across a reload for the same reason the baseline is: it is a count of
   * things that happened, and a reload is not one of them. It used to reset
   * to 0, so any completion after a reload reported that nobody did anything.
   */
  doneByDoing?: number;
  /**
   * When the reader started walking it, so a reload does not look like a
   * fresh visit. Held in state rather than only in a ref because the ref
   * resets on reload while `mode` and `step` are restored: the guide came
   * back mid-walkthrough with "nobody has started this yet" still true, and
   * the settle watch ran again and renumbered the steps under the reader --
   * reload Connections on "Fix the expired one" and step 3 read "Add another
   * account" until the rows arrived, then jumped back.
   */
  startedAt?: number;
  /** Every step this reader has been shown, for `resurface` -- see below. */
  seenSteps?: string[];
  /**
   * Set while a `resurface` step is showing on its own, so a reload keeps it
   * on its own rather than settling the whole guide around it.
   */
  resurfacing?: string;
}

/**
 * Per guide, per org, per person — never per browser.
 *
 * A second account on the same machine must not inherit where someone else got
 * to. Null until an org resolves: `orgId` is '' before the backfill lands, and
 * a fallback key would be shared by every org-less session on the machine.
 * Mirrors storageKey() in hooks/use-guides.ts.
 */
function stateKey(id: string, orgId?: string, userId?: string): string | null {
  return orgId ? `fastn-page-guide:${orgId}:${userId ?? "anon"}:${id}` : null;
}

function readState(key: string | null): GuideState {
  if (!key) return {};
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as GuideState) : {};
  } catch {
    return {};
  }
}

function writeState(key: string | null, next: GuideState): void {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* private mode — the guide still works, it just forgets */
  }
}

// ── Reading the page ────────────────────────────────────────────────────────

/** Does the page satisfy this signal right now? */
/** How many things match right now. -1 when the selector is unusable. */
function signalCount(sig: GuideSignal): number {
  let nodes: Element[] = [];
  try {
    nodes = Array.from(document.querySelectorAll(sig.selector));
  } catch {
    // A malformed selector is a copy bug in the guide, not a reason to crash
    // the page. "Cannot tell" leaves the step waiting, where Skip this step is
    // still there — the reader is never stuck.
    return -1;
  }
  if (sig.text) {
    const want = sig.text.toLowerCase();
    nodes = nodes.filter((n) => (n.textContent ?? "").toLowerCase().includes(want));
  }
  return nodes.length;
}

function signalMet(sig: GuideSignal, baseline: number): boolean {
  const n = signalCount(sig);
  if (n < 0) return false;
  if (sig.mode === "present") return n > 0;
  if (sig.mode === "absent") return n === 0;
  // "decrease" — the reader has to clear one, and the instruction is about
  // one. Fewer than when the step opened is them having done it; "absent"
  // would make everyone else's leftovers their problem.
  if (sig.mode === "decrease") return baseline >= 0 && n < baseline;
  // "increase" — the reader has to add one, so what counts is more than there
  // were when the step opened, not that any exist.
  return baseline >= 0 && n > baseline;
}

/**
 * The step to land on from `i`, moving in `dir`, past any whose `requires` the
 * page does not meet right now. `list.length` or -1 when there is none.
 *
 * Skipping "Open one" on Workflows left the next two steps pointing into an
 * editor that was never opened, at nothing.
 */
function landing(list: GuideStep[], i: number, dir: 1 | -1): number {
  while (i >= 0 && i < list.length) {
    const req = list[i]!.requires;
    if (!req || signalMet(req, -1)) return i;
    i += dir;
  }
  return i;
}

/**
 * Whether the page has loaded far enough to answer the guide's conditions:
 * its `readyWhen` is on screen, or failing that a "present" condition is met.
 * "absent" is true on a page that has not loaded yet, so it says nothing.
 */
function canAnswer(guide: Guide): boolean {
  if (guide.readyWhen) {
    try {
      return document.querySelector(guide.readyWhen) !== null;
    } catch {
      // A malformed selector is a copy bug in the guide; fall back to waiting.
      return false;
    }
  }
  const conditions = guide.steps
    .map((st) => st.onlyIf)
    .filter((c): c is GuideSignal => !!c && c.mode !== "absent");
  return conditions.length === 0 || conditions.some((c) => signalMet(c, -1));
}

/** The step's agent prompt with its placeholders read off the page. */
function fillPrompt(step: GuideStep): string {
  return (step.agentPrompt ?? "").replace(/\{(\w+)\}/g, (whole, key: string) => {
    const fill = step.agentPromptFill?.[key];
    if (!fill) return whole;
    let el: Element | null = null;
    try {
      el = document.querySelector(fill.selector);
    } catch {
      // A malformed selector is a copy bug in the guide; the fallback reads fine.
    }
    const raw = el instanceof HTMLInputElement ? el.value : (el?.textContent ?? "");
    const value = raw.trim();
    return value ? `"${value}"` : fill.fallback;
  });
}

/**
 * Watches the page for one signal.
 *
 * Polling rather than a MutationObserver on the document: the pages this runs
 * on re-render constantly (tables, live previews, polling queries), and an
 * observer would fire hundreds of times a second to answer a question that only
 * needs answering a few times. 400ms is below the threshold where the "Done"
 * feels like a lag.
 */
function useSignal(
  sig: GuideSignal | undefined,
  active: boolean,
  stored?: { key: string; count: number },
  onSample?: (key: string, count: number) => void,
): boolean {
  // The answer is stored WITH the signal it answers. Two waiting steps in a
  // row otherwise auto-completed the second one: on the render that moves to
  // it, this hook's effect has not run yet, so a bare `met` still holds the
  // previous step's `true`, and the advance effect reads it and fires at once
  // -- another did_it for something nobody did.
  const [answer, setAnswer] = useState<{ key: string; met: boolean }>({ key: "", met: false });
  // Every field that changes what the signal means. Without rebaseOn, two
  // signals differing only in it shared one effect run and one stored
  // baseline.
  const key = sig ? [sig.mode, sig.selector, sig.text ?? "", sig.rebaseOn ?? ""].join("|") : "";
  // What was already on the page when this step opened. Only "increase" reads
  // it, and it has to be sampled before the first check or the step's own
  // first poll becomes its baseline.
  const baseline = useRef(-1);
  // Which signal the baseline above was sampled for. The effect re-runs when
  // the anchor resolves, and re-sampling then would be wrong -- see below.
  const baselinedFor = useRef("");

  useEffect(() => {
    if (!sig || !active) {
      setAnswer({ key, met: false });
      baselinedFor.current = "";
      return;
    }
    // Sample once per signal, not once per subscription. "increase" compares
    // against what was on the page when the STEP opened; a second sample taken
    // after the reader has already acted forgets what they did and strands the
    // step.
    if (baselinedFor.current !== key) {
      // A baseline carried over from the session that started this step beats
      // anything measurable now -- see `waitBaseline`.
      if (stored && stored.key === key) {
        baseline.current = stored.count;
      } else {
        baseline.current = signalCount(sig);
        onSample?.(key, baseline.current);
      }
      baselinedFor.current = key;
    }
    // What the page was showing when the baseline was taken. A change here is
    // the page being re-filtered, not the reader acting, so the baseline moves
    // with it -- see `rebaseOn`.
    // Guarded like signalCount: a malformed selector is a copy bug in the
    // guide, not a reason to crash the page out from under the reader.
    const countRebase = (): number => {
      if (!sig.rebaseOn) return -1;
      try {
        return document.querySelectorAll(sig.rebaseOn).length;
      } catch {
        return -1;
      }
    };
    let population = countRebase();
    const check = () => {
      if (sig.rebaseOn) {
        const now = countRebase();
        if (now !== population) {
          population = now;
          baseline.current = signalCount(sig);
          // Persisted too, or a reload after a filter change restores the
          // count from before it.
          onSample?.(key, baseline.current);
        }
      }
      setAnswer({ key, met: signalMet(sig, baseline.current) });
    };
    check();
    const t = window.setInterval(check, 400);
    return () => window.clearInterval(t);
    // `key` stands in for the signal's identity: the object is new every render.
  }, [key, active]);

  return answer.key === key && answer.met;
}

/**
 * Re-renders on resize.
 *
 * Placement is computed from the window, so without this the modal keeps
 * wherever it landed on the very first paint — which, before the pane has
 * settled, is the top-left corner.
 */
function useViewport(): { w: number; h: number } {
  const [size, setSize] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const on = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    on();
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return size;
}

/**
 * Where the opening modal sits: under the page title, as designed.
 *
 * `.ph` is the page header every standard page renders; the builders that opt
 * out of it fall back to their own root, and anything with neither lands in the
 * middle rather than nowhere.
 */
function useIntroRect(active: boolean, anchor?: string): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useEffect(() => {
    if (!active) {
      setRect(null);
      return;
    }
    const find = () =>
      (anchor ? document.querySelector<HTMLElement>(`[data-testid="${anchor}"]`) : null) ??
      document.querySelector<HTMLElement>(".ph") ??
      document.querySelector<HTMLElement>(".fx-widgets, .fx-page");
    const measure = () => {
      const el = find();
      setRect(el ? el.getBoundingClientRect() : null);
    };
    measure();
    const t = window.setInterval(measure, 500);
    return () => window.clearInterval(t);
  }, [active, anchor]);
  return rect;
}

/** A step's anchor as a list of testids, whichever form the guide wrote it in. */
function anchorsOf(anchor: string | string[] | undefined): string[] {
  return Array.isArray(anchor) ? anchor : anchor ? [anchor] : [];
}

/** The box of the step's anchor, followed while it exists. */
function findAnchorEl(
  list: string[],
  anchorText: string | undefined,
): { el: HTMLElement; covered: boolean } | null {
  const want = anchorText?.toLowerCase();
  // A match that something is drawn over is passed over for a later one that
  // is visible. Workflows' rows stay mounted under the full-screen editor, so
  // "first that exists" pointed the step at a list nobody could see while the
  // editor held the same thing (its Executions tab) in plain view. With no
  // visible match, the first one still wins and the panel docks as before.
  let firstCovered: { el: HTMLElement; covered: boolean } | null = null;
  for (const a of list) {
    let hit: HTMLElement | null | undefined;
    if (a.endsWith("-")) {
      const all = Array.from(
        document.querySelectorAll<HTMLElement>(`[data-testid^="${a}"]`),
      );
      // Without anchorText this keeps the old "first of these" behaviour.
      hit = want
        ? all.find((el) => (el.textContent ?? "").toLowerCase().includes(want))
        : all[0];
    } else {
      hit = document.querySelector<HTMLElement>(`[data-testid="${a}"]`);
    }
    if (!hit) continue;
    const covered = isCovered(hit, hit.getBoundingClientRect());
    if (!covered) return { el: hit, covered };
    firstCovered ??= { el: hit, covered };
  }
  return firstCovered;
}

/**
 * The part of the window an element can be seen through: the window, cut
 * down by every ancestor that clips its overflow. Comparing against the
 * window alone missed a control scrolled out of view inside a scrolling list
 * that is itself on screen.
 */
function viewOf(el: HTMLElement): { top: number; left: number; bottom: number; right: number } {
  let v = { top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth };
  for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
    const cs = getComputedStyle(n);
    if (cs.overflowX === "visible" && cs.overflowY === "visible") continue;
    const b = n.getBoundingClientRect();
    v = {
      top: Math.max(v.top, b.top),
      left: Math.max(v.left, b.left),
      bottom: Math.min(v.bottom, b.bottom),
      right: Math.min(v.right, b.right),
    };
  }
  return v;
}

/**
 * How to scroll the anchor into view when the page itself hides it, or null
 * when it does not: scrolled out of its view, or under a sticky part of the
 * page's own layout -- as opposed to a dialog or other overlay, which
 * scrolling cannot fix.
 *
 * The widget builder's Add sat under its sticky tab header after the panel
 * had scrolled, so the step ringed nothing at the one control it was about.
 *
 * A control bigger than its view (the Connections table, dozens of rows
 * long) cannot fit, so it only counts when it is entirely outside, and is
 * brought in by its start. Treating it like a button scrolled the page to the
 * middle of the table on every visit to the step.
 */
function scrollHint(el: HTMLElement): ScrollIntoViewOptions | null {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;
  const view = viewOf(el);
  const tall = rect.height > view.bottom - view.top;
  const wide = rect.width > view.right - view.left;
  const block: ScrollLogicalPosition = tall ? "start" : "center";
  const inline: ScrollLogicalPosition = wide ? "start" : "nearest";
  // Even partly outside counts for a control that fits: half a button under
  // the top edge is under the app's top bar, which is the page's own chrome.
  const outY = tall
    ? rect.bottom <= view.top || rect.top >= view.bottom
    : rect.top < view.top || rect.bottom > view.bottom;
  const outX = wide
    ? rect.right <= view.left || rect.left >= view.right
    : rect.left < view.left || rect.right > view.right;
  if (outY || outX) return { block, inline };
  if (typeof document.elementFromPoint !== "function") return null;
  // The middle of the part that can be seen, not of the whole control.
  const x = (Math.max(rect.left, view.left) + Math.min(rect.right, view.right)) / 2;
  const y = (Math.max(rect.top, view.top) + Math.min(rect.bottom, view.bottom)) / 2;
  const top = document.elementFromPoint(x, y);
  if (!top || el.contains(top) || top.contains(el)) return null;
  if (top.closest('[data-testid^="page-guide-"], [role="dialog"], [aria-modal="true"]')) return null;
  // A fixed layer over most of the screen is an overlay (the dashboard's
  // dialogs are full-screen fixed backdrops, mostly without role="dialog");
  // a fixed bar is chrome the page scrolls under.
  for (let n: Element | null = top; n; n = n.parentElement) {
    if (getComputedStyle(n).position !== "fixed") continue;
    const box = n.getBoundingClientRect();
    return box.width * box.height < 0.5 * window.innerWidth * window.innerHeight ? { block, inline } : null;
  }
  return { block, inline };
}

/**
 * Whether something is drawn on top of the anchor right now.
 *
 * A waiting step asks the reader to act, and most of those actions raise a
 * surface over the page: the workflow editor is a full-screen opaque overlay
 * (`.fx-ed`, z-index 1500), and Create/Connect/Add open dialogs at 1000-2000.
 * The guide sat at 120, under all of them, so the moment the reader did the
 * thing the step asked for, the panel saying "Done. On to the next step"
 * was behind the surface their action had just opened. They saw nothing and
 * reported the guide as not moving.
 *
 * Raising the panel over those layers is half the fix; the other half is this.
 * Once the page is covered the anchor is not visible, so an arrow pointing at
 * it points at nothing, and the panel at the anchor's old coordinates can land
 * on the dialog's own controls. When this is true the panel docks to a corner
 * and drops its arrow and ring instead.
 */
function isCovered(el: HTMLElement, rect: DOMRect): boolean {
  const x = rect.left + rect.width / 2;
  const y = rect.top + rect.height / 2;
  // Off-screen is not covered -- it is scrolled away, which place() clamps for.
  if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight) return false;
  // jsdom has no hit testing, so under test this question has no answer and
  // "nothing is covering it" is the honest one.
  if (typeof document.elementFromPoint !== "function") return false;
  const top = document.elementFromPoint(x, y);
  if (!top || el.contains(top) || top.contains(el)) return false;
  // The guide's own panel sitting over its anchor is not the page being covered.
  if (top.closest('[data-testid^="page-guide-"]')) return false;
  return true;
}

function useAnchorRect(
  anchor: string | string[] | undefined,
  active: boolean,
  anchorText?: string,
): { rect: DOMRect | null; covered: boolean } {
  const [rect, setRect] = useState<DOMRect | null>(null);
  // Which anchor `covered` was measured for. On the render that moves to a
  // new step the effect has not run yet, so the previous step's answer is
  // still in state; reading it then docked the new step for good.
  const [covered, setCovered] = useState<{ key: string; value: boolean }>({ key: "", value: false });
  const list = useMemo(() => anchorsOf(anchor), [anchor]);
  const key = `${list.join("|")}|${anchorText ?? ""}`;

  useEffect(() => {
    if (list.length === 0 || !active) {
      setRect(null);
      setCovered({ key, value: false });
      return;
    }
    // A trailing dash means "the first of these", which is how a step points at
    // the first row of a list without knowing any ids.
    let stopped = false;
    const measure = () => {
      if (stopped) return;
      const found = findAnchorEl(list, anchorText);
      setRect(found ? found.el.getBoundingClientRect() : null);
      setCovered({ key, value: found?.covered ?? false });
    };
    measure();
    const t = window.setInterval(measure, 500);
    const onMove = () => measure();
    // Captured: the scroller is `.app-content`, not the window.
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      stopped = true;
      window.clearInterval(t);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
    // `key` stands in for the list, which is a new array every render.
  }, [key, active]);

  return { rect, covered: covered.key === key && covered.value };
}

// ── Placing the modal ───────────────────────────────────────────────────────

const MODAL_W = 308;
const GAP = 14;

// The two layers this component draws on, named once because they are a pair
// and only make sense together.
//
// The guide outranks everything a reader can be asked to open, and nothing
// else. The dashboard's layers, measured: topbar 100, sidebar flyout 120,
// menus and toasts 1000-1300, the full-screen workflow editor 1500
// (`.fx-ed`, opaque), `.modal-overlay` dialogs 2000, tooltips 3000+. The
// panel sat at 120 -- under every surface a waiting step's own action opens,
// so "Done. On to the next step" was drawn behind the thing the reader had
// just opened. 2100 clears the dialogs and stays under tooltips, which should
// still win.
//
// Written out rather than computed because Tailwind reads the class name from
// the source text; an interpolated value compiles to nothing. That makes this
// a number coupled to styles.css by hand, so if a dialog's z-index moves, move
// Z_PANEL with it -- nothing fails if you do not, the guide just silently
// falls behind, which is the bug this constant exists to have fixed.
const Z_PANEL = "z-[2100]";
// Above the sidebar flyout (120), below everything a reader's own action
// opens. It sat at 115 and so painted underneath the flyout, which is exactly
// where the steps that ring `sidebar-*-link` put it -- an invisible highlight
// on the two steps that point into the sidebar. It still stays well under the
// editor and dialogs on purpose: the ring belongs to the page, and one
// floating over a dialog would highlight a control that is not there.
const Z_RING = "z-[130]";

type Placement = { top: number; left: number; arrow: React.CSSProperties | null };

/**
 * Beside the anchor where there is room, below it otherwise, and in the middle
 * of the screen when there is no anchor at all.
 *
 * A step whose control is missing still shows: the words are the point, and a
 * modal that vanishes because a selector moved is the failure the old panel
 * had.
 */
function place(
  rect: DOMRect | null,
  height: number,
  vw: number,
  vh: number,
  dock = false,
): Placement {
  const h = height || 240;

  // The reader is acting somewhere this step cannot point: either something is
  // drawn over the anchor (see isCovered) or the action took them off the page
  // that held it. Sit in the bottom-left corner and drop the arrow, which would
  // otherwise point at a control nobody can see -- and, worse, put the panel on
  // top of the form or dialog the step just told them to fill in. Measured on
  // Customers: Create opens a full page, the anchor goes with the old route,
  // and the centred fallback landed squarely on the Customer Name field.
  // Bottom-LEFT because the minimised pill and the support launcher are both
  // bottom-right.
  if (dock) {
    return {
      top: Math.max(GAP, vh - h - GAP),
      left: GAP,
      arrow: null,
    };
  }

  if (!rect || rect.width === 0) {
    return {
      top: Math.max(GAP, (vh - h) / 2),
      left: Math.max(GAP, (vw - MODAL_W) / 2),
      arrow: null,
    };
  }

  const clampTop = (t: number) => Math.min(Math.max(GAP, t), Math.max(GAP, vh - h - GAP));

  // To the right of the anchor.
  if (rect.right + GAP + MODAL_W <= vw - GAP) {
    const top = clampTop(rect.top + rect.height / 2 - h / 2);
    return {
      top,
      left: rect.right + GAP,
      arrow: { left: -6, top: Math.min(Math.max(12, rect.top + rect.height / 2 - top - 6), h - 18) },
    };
  }
  // To the left.
  if (rect.left - GAP - MODAL_W >= GAP) {
    const top = clampTop(rect.top + rect.height / 2 - h / 2);
    return {
      top,
      left: rect.left - GAP - MODAL_W,
      arrow: { right: -6, top: Math.min(Math.max(12, rect.top + rect.height / 2 - top - 6), h - 18) },
    };
  }
  // Below, then above.
  const left = Math.min(Math.max(GAP, rect.left), vw - MODAL_W - GAP);
  const below = rect.bottom + GAP;
  if (below + h <= vh - GAP) {
    return {
      top: below,
      left,
      arrow: { top: -6, left: Math.min(Math.max(12, rect.left + rect.width / 2 - left - 6), MODAL_W - 18) },
    };
  }
  return {
    top: clampTop(rect.top - GAP - h),
    left,
    arrow: { bottom: -6, left: Math.min(Math.max(12, rect.left + rect.width / 2 - left - 6), MODAL_W - 18) },
  };
}

// ── The guide ───────────────────────────────────────────────────────────────

export function PageGuide(): React.ReactElement | null {
  const location = useLocation();
  const { userContext } = useAuth();
  const role = userContext?.role as SystemRole | undefined;

  const guide = useMemo(
    () => guideForRoute(location.pathname, location.search, role),
    [location.pathname, location.search, role],
  );

  // The off switch, for prod, without a deploy.
  //
  // Read straight off the resolved map rather than through `useEntitlement`,
  // because that helper answers `enabled: false` for "loading", "errored" and
  // "no org yet" alike — correct for a paid capability, which should stay
  // hidden until you know, and wrong here, where the same silence deletes the
  // help from every page. Only an explicit `false` hides it; absent, loading,
  // errored and unasked all leave it on, matching safeDefault() in @fastn/rbac.
  const entitlements = useResolvedEntitlements();
  const turnedOff =
    entitlements.isSuccess && entitlements.data?.["guidance.pageGuide"] === false;

  if (!guide || turnedOff) return null;
  // Remounts per guide and per account, so one page's position never leaks into
  // another's and a second account starts clean.
  return (
    <GuideRunner
      key={`${userContext?.orgId ?? ""}:${userContext?.sub ?? ""}:${guide.id}`}
      guide={guide}
      storeKey={stateKey(guide.id, userContext?.orgId, userContext?.sub)}
    />
  );
}

function GuideRunner({ guide, storeKey }: { guide: Guide; storeKey: string | null }): React.ReactElement | null {
  const navigate = useNavigate();
  const [state, setState] = useState<GuideState>(() => readState(storeKey));

  const update = useCallback(
    (patch: GuideState) => {
      setState((prev) => {
        const merged = { ...prev, ...patch };
        writeState(storeKey, merged);
        return merged;
      });
    },
    [storeKey],
  );

  /**
   * Steps that apply to this reader, settled ONCE when the guide opens.
   *
   * Re-deciding on every render would renumber the guide underneath them: the
   * "fix the expired one" step disappears the moment they fix it, and a guide
   * that goes from "Step 3 of 4" to "Step 3 of 3" mid-sentence reads as a bug.
   */
  const [steps, setSteps] = useState<GuideStep[] | null>(null);
  // Bumped by Help to run the settle watch again. Clearing `steps` alone did
  // not: the watch had already settled and stopped re-settling once the guide
  // was started, so the list stayed unfiltered -- every conditional step,
  // including "Connect your first account" for someone with connections.
  const [settleRun, setSettleRun] = useState(0);
  // Read by settleSteps, which is memoised on the guide alone.
  const resurfacingRef = useRef(state.resurfacing);
  resurfacingRef.current = state.resurfacing;

  const settleSteps = useCallback(() => {
    // -1: a condition asks what is on the page right now, so "increase" is
    // meaningless here and is never satisfied. The copy test enforces that no
    // onlyIf uses it.
    setSteps((prev) => {
      const next = resurfacingRef.current
        ? guide.steps.filter((s) => s.id === resurfacingRef.current)
        : guide.steps.filter((s) => !s.onlyIf || signalMet(s.onlyIf, -1));
      // Same answer as last time, so hand back the same array. The watch below
      // runs every 150ms for up to four seconds, and a fresh array each tick
      // re-rendered the opening card ~20 times to say nothing had changed.
      const same =
        prev !== null && prev.length === next.length && prev.every((s, i) => s.id === next[i].id);
      return same ? prev : next;
    });
  }, [guide.steps]);

  useEffect(() => {
    // When to decide which steps this reader gets, which has two answers
    // because a fresh guide and a resumed one want opposite things.
    //
    // Settling once after a fixed delay was a bet on the list arriving first:
    // when the fetch landed later, every `onlyIf` read an empty DOM and its
    // step was dropped for good, so the guide reported a smaller total on a
    // slow response than on a fast one. Waiting instead until a condition is
    // satisfied fixes that and breaks something worse FOR A FRESH GUIDE --
    // until it settles the opening card lists every step, conditional ones
    // included, and the count then changes while the reader is reading it.
    //
    // A RESUMED guide has no opening card. Someone reloaded mid-walkthrough,
    // `mode` and `step` came back from storage, and settling early against a
    // page whose rows have not arrived drops their conditional step and slides
    // a different one under their position -- reload Connections on "Fix the
    // expired one" and it reads "Add another account" until the rows land.
    // There is nothing to protect from a late count here and everything to
    // lose by guessing, so a resumed guide waits for the page instead.
    const resumed = guideStartedAt.current !== null;
    const SETTLE = 600;
    const WATCH_UNTIL = 4000;
    const started = Date.now();
    let settled = false;
    const tick = () => {
      const age = Date.now() - started;
      if (!settled) {
        // Fresh: on the clock. Resumed: once the page can actually answer the
        // conditions, or at the deadline so an unmet one is not waited on
        // forever.
        const ready = resumed ? canAnswer(guide) || age >= WATCH_UNTIL : age >= SETTLE;
        if (!ready) return;
      } else if (guideStartedAt.current !== null) {
        // Renumbering someone mid-walkthrough is the thing this must not do.
        return;
      }
      settled = true;
      settleSteps();
    };
    // No separate one-shot at SETTLE: the 150ms interval already ticks there.
    const id = window.setInterval(tick, 150);
    const stop = window.setTimeout(() => {
      // The deadline is also the last chance for a resumed guide to settle.
      tick();
      window.clearInterval(id);
    }, WATCH_UNTIL);
    return () => {
      window.clearTimeout(stop);
      window.clearInterval(id);
    };
  }, [settleSteps, guide.steps, guide.readyWhen, settleRun]);

  // Offer the guide once per page, per account. An explicit close is remembered
  // and beats the offer; the top-bar button brings it back either way.
  useEffect(() => {
    if (state.seen) return;
    update({ seen: true, mode: "intro", step: 0, stepId: guide.steps[0]?.id });
    capture("page_guide_offered", { guide: guide.id, step_total: guide.steps.length });
  }, [state.seen, update, guide.id, guide.steps.length]);

  useEffect(() => {
    const onOpen = () => {
      // Settles only when the page can already answer. Help is most useful
      // right after arriving, before the rows land, and settling then dropped
      // the conditional step and renumbered the guide under the reader.
      // Otherwise the list is cleared and the watch runs again to wait.
      if (canAnswer(guide)) {
        settleSteps();
      } else {
        setSteps(null);
        setSettleRun((n) => n + 1);
      }
      if (guideStartedAt.current === null) begin("help_menu");
      update({ mode: "open", finished: false });
    };
    window.addEventListener(OPEN_GUIDE_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_GUIDE_EVENT, onOpen);
  }, [update]);

  const mode: Mode = state.mode ?? "closed";

  // While the opener is up nobody is on a step yet, so its list may follow the
  // page: the settle watch stops after four seconds, and switching the
  // Connections view after that left it listing the other view's steps.
  useEffect(() => {
    if (mode !== "intro") return;
    const t = window.setInterval(settleSteps, 500);
    return () => window.clearInterval(t);
  }, [mode, settleSteps]);
  const list = steps ?? guide.steps;
  // For timers that fire after the render that set them.
  const listRef = useRef(list);
  listRef.current = list;
  const byId = state.stepId ? list.findIndex((st) => st.id === state.stepId) : -1;
  const index =
    byId >= 0 ? byId : Math.min(state.step ?? 0, Math.max(0, list.length - 1));
  const step: GuideStep | undefined = list[index];

  // Declared here, above the early return below, because the OPEN_GUIDE_EVENT
  // listener captures it on the FIRST render and that render usually returns
  // early: a reader who pressed Not now has mode "closed". Declared after that
  // return, this const is never initialised on the render the listener kept,
  // so Help -> Take a tour hit the temporal dead zone and threw instead of
  // reopening the guide -- the main way back into a dismissed one.
  const begin = (source: "offer" | "help_menu") => {
    const at = Date.now();
    guideStartedAt.current = at;
    startedInAnotherSession.current = false;
    update({ startedAt: at, doneByDoing: 0 });
    doneByDoing.current = 0;
    capture("page_guide_started", { guide: guide.id, step_total: list.length, source });
  };
  // Last when nothing after it can be landed on, so the button says Done
  // rather than Next on a step whose followers the page cannot show.
  // A `resettle` step continues into the list as the page is now, so that is
  // the list its label and counter answer for -- unless its own `onlyIf` has
  // stopped holding and that list no longer has it in. Then the list it is
  // actually in, or it read "Step 0 of N" and judged "last" from nowhere.
  const replanned = step?.resettle
    ? guide.steps.filter((st) => !st.onlyIf || signalMet(st.onlyIf, -1))
    : null;
  const ahead = replanned?.some((st) => st.id === step?.id) ? replanned : list;
  const aheadFrom = ahead === list ? index : ahead.findIndex((st) => st.id === step?.id);
  const last = landing(ahead, aheadFrom + 1, 1) >= ahead.length;
  // Where Back goes; -1 hides it, rather than landing on a step that cannot show.
  const prev = landing(list, index - 1, -1);
  // What the counter and the bars show: steps that can show right now, plus
  // the one on screen. Counting the rest made a one-step guide on an empty
  // Customers view read "1 of 3".
  const shown = ahead.filter(
    (s) => s.id === step?.id || !s.requires || signalMet(s.requires, -1),
  );
  const position = step ? shown.indexOf(step) + 1 : 0;
  const total = shown.length;

  const running = mode === "open" || mode === "pill";
  const waiting = !!step?.wait;
  const anchor = mode === "open" ? step?.anchor : undefined;
  const { rect, covered } = useAnchorRect(anchor, mode === "open", step?.anchorText);

  // Once per step: bring the anchor into view if the page is hiding it. Not
  // repeated, so a reader who scrolls away is not dragged back. Retried for a
  // couple of seconds because the anchor can render after the step opens.
  useEffect(() => {
    if (mode !== "open" || !step?.anchor) return;
    const list = anchorsOf(step.anchor);
    let tries = 0;
    const t = window.setInterval(() => {
      const found = findAnchorEl(list, step.anchorText);
      if (found || ++tries > 8) window.clearInterval(t);
      const hint = found ? scrollHint(found.el) : null;
      // Instant, not smooth: a smooth scroll started as the step changes was
      // cancelled part-way often enough in the widget builder to leave Add
      // where it was, under the sticky header.
      if (found && hint) found.el.scrollIntoView({ ...hint, behavior: "auto" });
    }, 250);
    return () => window.clearInterval(t);
  }, [mode, step?.id]);
  // Not until the guide has settled. A comparison against a page that has not
  // rendered yet is not a comparison: restoring the real baseline made a
  // resumed "decrease" step read the empty page as "fewer than before" and
  // complete itself on arrival. Settling is already the point at which the
  // page can answer questions about itself -- for a resumed guide it waits for
  // exactly that -- so it is also the point at which a signal may start.
  const met = useSignal(
    step?.wait?.done,
    running && waiting && steps !== null,
    state.waitBaseline,
    (key, count) => update({ waitBaseline: { key, count } }),
  );

  // A step whose `requires` stops holding moves to the nearest one that can
  // show -- back first, since that is usually the step that sets it up. A
  // reload restores the step but not the editor it lives in, and closing the
  // editor leaves its steps pointing at nothing; going through landing() on
  // every move did not cover either.
  useEffect(() => {
    const req = step?.requires;
    if (mode !== "open" || !req || steps === null) return;
    const check = () => {
      if (signalMet(req, -1)) return;
      const back = landing(list, index - 1, -1);
      const to = back >= 0 ? back : landing(list, index + 1, 1);
      if (to < list.length) update({ step: to, stepId: list[to]?.id });
    };
    check();
    const t = window.setInterval(check, 500);
    return () => window.clearInterval(t);
  }, [mode, step?.id, steps, index, list, update]);

  // "Done." then on by itself. Deliberately also when minimised: the reader
  // went to do the thing, and coming back to a pill that already moved on is
  // the whole point of the pill.
  //
  // The timer is held in a ref and cleared only on unmount. It used to be the
  // effect's cleanup, which runs whenever a dependency changes — so a re-render
  // inside the 1.2s window cancelled the advance and left `justDone` stuck
  // true, with the footer hidden and no way to continue. The guard ref meant
  // the effect would not re-arm it either, so the guide simply froze.
  /* Measured, not guessed. "How long did people sit on this step" is the
     question that separates a step that is hard from one that is merely long,
     and neither is visible from counts alone. */
  const stepEnteredAt = useRef<number>(Date.now());
  // Seeded from storage so a reload resumes the walkthrough it was in the
  // middle of, rather than presenting as one nobody has started.
  const guideStartedAt = useRef<number | null>(state.startedAt ?? null);
  // Captured at mount: `begin()` clears it for a genuinely new walkthrough.
  const startedInAnotherSession = useRef(state.startedAt != null);
  const doneByDoing = useRef(state.doneByDoing ?? 0);

  const [justDone, setJustDone] = useState(false);
  // Per step, not one shared slot: see goto().
  const advanced = useRef<Set<string>>(new Set());
  const advanceTimer = useRef<number | null>(null);
  useEffect(() => () => {
    if (advanceTimer.current !== null) window.clearTimeout(advanceTimer.current);
  }, []);

  // A step change always clears the banner, whoever caused it.
  useEffect(() => {
    setJustDone(false);
  }, [step?.id]);

  // Which waiting step the reader has pressed the ringed control on. Watching
  // the page cannot always tell that the thing is done -- the action can land
  // somewhere the signal does not look, or in another tab -- and a step that
  // only offered Skip then left someone who had just done it guessing whether
  // it had worked. Once they have acted, the step offers Done instead.
  const [actedOn, setActedOn] = useState<string | null>(null);
  // Per visit to the step: Back onto one acted on before showed Done again
  // and pressing it logged a second `confirmed`.
  useEffect(() => {
    setActedOn(null);
  }, [step?.id]);
  const anchorList = useMemo(
    () => anchorsOf(step?.anchor),
    [step?.anchor],
  );
  useEffect(() => {
    if (!running || !waiting || !step || anchorList.length === 0) return;
    const id = step.id;
    const onPress = (e: Event) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (step.actIgnore && t.closest(step.actIgnore)) return;
      // Any match, not only the ringed one: "press Connect on the app you
      // want" rings the first card and means every card.
      const hit = anchorList.some((a) =>
        a.endsWith("-") ? t.closest(`[data-testid^="${a}"]`) : t.closest(`[data-testid="${a}"]`),
      );
      if (hit) setActedOn(id);
    };
    document.addEventListener("pointerdown", onPress, true);
    return () => document.removeEventListener("pointerdown", onPress, true);
  }, [running, waiting, step?.id, anchorList]);
  const acted = !!step && actedOn === step.id;

  useEffect(() => {
    if (mode !== "open" || !step) return;
    stepEnteredAt.current = Date.now();
    capture("page_guide_step_viewed", {
      guide: guide.id,
      step: step.id,
      step_index: index,
      step_total: list.length,
      waits: !!step.wait,
      resurfaced: state.resurfacing === step.id,
    });
    if (!(state.seenSteps ?? []).includes(step.id)) {
      update({ seenSteps: [...(state.seenSteps ?? []), step.id] });
    }
  }, [mode, step?.id, guide.id, index, list.length]);

  // A `resurface` step that did not apply while the reader walked the guide
  // shows up by itself once it does: a connection that expires next week
  // brings back "Fix the expired one", alone. Only for readers who started
  // the guide -- Not now is an answer -- and once per step: it is marked seen
  // when it shows, so closing it is the end of it.
  // Whether the reader walked the guide on this visit. Closing it part-way
  // with an expired row on screen would otherwise bring that step straight
  // back; resurfacing is for a later visit, when the situation is new.
  const walkedThisVisit = useRef(false);
  useEffect(() => {
    if ((mode === "open" || mode === "pill") && !state.resurfacing) walkedThisVisit.current = true;
  }, [mode, state.resurfacing]);
  useEffect(() => {
    if (mode !== "closed" || state.finished || state.startedAt == null) return;
    if (walkedThisVisit.current) return;
    const candidates = guide.steps.filter((st) => st.resurface && st.onlyIf);
    if (candidates.length === 0) return;
    const check = () => {
      if (!canAnswer(guide)) return;
      const seen = state.seenSteps ?? [];
      const due = candidates.find((st) => !seen.includes(st.id) && signalMet(st.onlyIf!, -1));
      if (!due) return;
      resurfacingRef.current = due.id;
      setSteps([due]);
      update({ mode: "open", step: 0, stepId: due.id, resurfacing: due.id });
    };
    check();
    const t = window.setInterval(check, 2000);
    return () => window.clearInterval(t);
  }, [mode, state.finished, state.startedAt, state.seenSteps, guide, update]);

  // A resurfaced step is a one-off; once it closes, the guide is whole again.
  useEffect(() => {
    if (mode === "closed" && state.resurfacing) update({ resurfacing: undefined });
  }, [mode, state.resurfacing, update]);

  useEffect(() => {
    if (!running || !waiting || !met || !step) return;
    if (advanced.current.has(step.id)) return;
    advanced.current.add(step.id);
    setJustDone(true);
    update({ mode: "open" });
    if (advanceTimer.current !== null) window.clearTimeout(advanceTimer.current);
    leaveStep("did_it");
    // From the step that finished, found in the list as it is when the timer
    // fires: the `requires` watch can move the reader within these 1.2s, and
    // advancing from the index captured here went on from the wrong place.
    const doneId = step.id;
    advanceTimer.current = window.setTimeout(() => {
      advanceTimer.current = null;
      setJustDone(false);
      const now = listRef.current;
      const at = now.findIndex((st) => st.id === doneId);
      const to = landing(now, (at >= 0 ? at : index) + 1, 1);
      if (to < now.length) {
        update({ step: to, stepId: now[to]?.id });
      } else {
        end();
      }
    }, 1200);
  }, [running, waiting, met, step, index, list.length, update]);

  // The finish note confirms the end; it does not live on the page afterwards.
  // Lost once already: it was spliced out with the block around it while fixing
  // the advance timer, and the note then sat in the corner indefinitely.
  useEffect(() => {
    if (!(mode === "closed" && state.finished)) return;
    const t = window.setTimeout(() => update({ finished: false }), 4000);
    return () => window.clearTimeout(t);
  }, [mode, state.finished, update]);


  const modalRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (!modalRef.current) return;
    setHeight(modalRef.current.getBoundingClientRect().height);
  }, [mode, index, justDone, step?.id]);

  // Once something has covered this step's anchor, the panel stays docked for
  // the rest of the step. It used to jump back beside the anchor the moment
  // the dialog closed, half a second later, and a click aimed at Done landed
  // on whatever had been under it -- closing the dialog the step had opened.
  const [dockedOn, setDockedOn] = useState<string | null>(null);
  // Cleared on every step change, or Back onto a step covered once before
  // opened it docked with its anchor in plain view.
  useEffect(() => {
    setDockedOn(null);
  }, [step?.id]);
  useEffect(() => {
    if (mode === "open" && step && covered) setDockedOn(step.id);
  }, [mode, step?.id, covered]);
  const dock = covered || (waiting && !rect) || (!!step && dockedOn === step.id);

  // Only docking and undocking are animated: the panel crosses the screen
  // then, and the eye has to follow it. Following its control on scroll is
  // not -- the ring tracks instantly, and an eased panel trailed it by 200ms
  // so the two visibly came apart.
  //
  // Decided during render, not in an effect: the transition has to be on the
  // element in the same frame its position changes, or there is nothing to
  // animate. Switched off by a render of its own when the window closes: left
  // to the next anchor re-measure (up to 500ms, or never on a step without an
  // anchor), the panel still eased behind the ring on a scroll in that gap.
  const wasDocked = useRef(dock);
  const glideUntil = useRef(0);
  if (wasDocked.current !== dock) {
    wasDocked.current = dock;
    glideUntil.current = Date.now() + 300;
  }
  const glideEnd = glideUntil.current;
  const glide = Date.now() < glideEnd;
  const [, endGlide] = useState(0);
  useEffect(() => {
    if (!glide) return;
    const t = window.setTimeout(() => endGlide((n) => n + 1), glideEnd - Date.now() + 16);
    return () => window.clearTimeout(t);
  }, [glide, glideEnd]);

  const view = useViewport();
  const introRect = useIntroRect(mode === "intro", guide.introAnchor);
  const pos = useMemo(
    () =>
      mode === "intro"
        ? introRect
          ? {
              top: Math.min(introRect.bottom + 8, Math.max(GAP, view.h - height - GAP)),
              left: Math.min(introRect.left, Math.max(GAP, view.w - MODAL_W - GAP)),
              arrow: null,
            }
          : place(null, height, view.w, view.h)
        : place(rect, height, view.w, view.h, dock),
    [mode, introRect, rect, height, view.w, view.h, dock],
  );

  if (mode === "closed" && !state.finished) return null;

  const close = () => {
    capture("page_guide_dismissed", {
      guide: guide.id,
      at: mode === "intro" ? "offer" : "step",
      step: mode === "intro" ? null : (step?.id ?? null),
      step_index: mode === "intro" ? null : index,
    });
    update({ mode: "closed" });
  };

  const start = () => {
    // Settled here rather than through settleSteps so the first step can be
    // chosen from the same list: step 0 may not be able to show (Customers'
    // row steps on an empty page), and starting there flashed it first.
    const next = guide.steps.filter((st) => !st.onlyIf || signalMet(st.onlyIf, -1));
    setSteps(next);
    begin("offer");
    const first = Math.min(landing(next, 0, 1), Math.max(0, next.length - 1));
    update({ mode: "open", step: first, stepId: next[first]?.id });
  };
  const leaveStep = (how: "next" | "did_it" | "confirmed" | "skipped") => {
    if (!step) return;
    capture("page_guide_step_completed", {
      guide: guide.id,
      step: step.id,
      step_index: index,
      step_total: list.length,
      how,
      seconds_on_step: Math.round((Date.now() - stepEnteredAt.current) / 1000),
    });
    if (how === "did_it") {
      doneByDoing.current += 1;
      update({ doneByDoing: doneByDoing.current });
    }
  };

  // How a run ends. A resurfaced step is a reminder, not a walk through the
  // guide: it logged page_guide_completed (timed from a start days earlier)
  // and showed the "guide done" toast every time it came back.
  const end = () => {
    if (state.resurfacing) {
      update({ mode: "closed" });
      return;
    }
    finish();
    update({ mode: "closed", finished: true });
  };

  const finish = () => {
    capture("page_guide_completed", {
      guide: guide.id,
      step_total: list.length,
      steps_done_by_doing: doneByDoing.current,
      seconds_total: Math.round((Date.now() - (guideStartedAt.current ?? Date.now())) / 1000),
      // Says whether the number above is wall-clock across a reload rather
      // than time spent. A tab left open overnight otherwise reports a guide
      // that took nine hours, and nothing downstream can tell that apart from
      // a reader who struggled.
      resumed: startedInAnotherSession.current,
    });
  };

  const goto = (i: number) => {
    setJustDone(false);
    // Deliberately does NOT clear `advanced`. It used to, so that the next
    // step could auto-advance -- but a Set keyed by step id arms each step on
    // its own, and clearing a shared flag re-armed steps already finished:
    // Back onto one you had done showed "Done." and threw you forward again
    // 1.2s later, logging a second did_it every time.
    //
    // Except a step that counts a change ("increase" / "decrease"): it is
    // re-armed with a fresh baseline from the page as it is now, so it waits
    // for a NEW change and cannot bounce. Kept armed-off, going Back to "Pick
    // the apps" and adding another app never moved the guide on. "present"
    // steps stay off: what they wait for is still on the page.
    const target = list[i];
    const mode = target?.wait?.done.mode;
    if (target && (mode === "increase" || mode === "decrease")) {
      advanced.current.delete(target.id);
      update({ step: i, stepId: target.id, mode: "open", waitBaseline: undefined });
      return;
    }
    update({ step: i, stepId: list[i]?.id, mode: "open" });
  };
  // Past a waiting step without the page having seen it done: Skip, or Done
  // once the reader has acted. Marked as advanced so a signal that lands a
  // moment later does not throw them a second step forward.
  const pass = (how: "confirmed" | "skipped") => {
    if (!step) return;
    advanced.current.add(step.id);
    leaveStep(how);
    if (last) {
      end();
    } else {
      goto(landing(list, index + 1, 1));
    }
  };

  const forward = () => {
    leaveStep("next");
    if (step?.resettle) {
      // See GuideStep.resettle: the rest of the list, worked out again for
      // what the page shows now, continuing from this step.
      const next = guide.steps.filter((st) => !st.onlyIf || signalMet(st.onlyIf, -1));
      const here = next.findIndex((st) => st.id === step.id);
      const to = landing(next, here + 1, 1);
      setSteps(next);
      if (here >= 0 && to < next.length) {
        update({ step: to, stepId: next[to]?.id, mode: "open" });
        return;
      }
    }
    if (!last) return goto(landing(list, index + 1, 1));
    if (!state.resurfacing) finish();
    if (step?.to) {
      capture("page_guide_handed_off", {
        guide: guide.id,
        step: step.id,
        to: step.to,
        via: "take_me_there",
      });
    }
    update({ mode: "closed", finished: !state.resurfacing });
    if (step?.to) navigate(step.to);
  };

  const body = document.body;

  // ── The opener ────────────────────────────────────────────────────────────
  if (mode === "intro") {
    return createPortal(
      <div
        ref={modalRef}
        role="dialog"
        aria-label={`${guide.title} guide`}
        className={cn(
          `fixed ${Z_PANEL} w-[308px] rounded-lg border border-border bg-card p-4`,
          "shadow-lg", // design-tokens-ignore: the guide floats over the page, the DS overlay exception
        )}
        style={{ top: pos.top, left: pos.left }}
        data-testid={ids.panel(guide.id)}
      >
        <p className="text-[12px] font-semibold uppercase tracking-wide text-fg-muted">
          Page guide · {total} {total === 1 ? "step" : "steps"}
        </p>
        <h2 className="mt-2 text-[16px] font-semibold text-foreground">New to {guide.title}?</h2>
        <p className="mt-1 text-[13px] leading-relaxed text-fg-muted">{guide.intro}</p>
        <ol className="my-3 flex flex-col gap-1.5">
          {shown.map((s, i) => (
            <li key={s.id} className="flex items-center gap-2.5 text-[13px] text-foreground">
              <span className="flex size-5 shrink-0 items-center justify-center rounded-pill border border-border text-[12px] text-fg-muted">
                {i + 1}
              </span>
              {s.title}
            </li>
          ))}
        </ol>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" data-testid={ids.notNowButton(guide.id)} onClick={close}>
            Not now
          </Button>
          <Button size="sm" data-testid={ids.startButton(guide.id)} onClick={start}>
            Show me
          </Button>
        </div>
      </div>,
      body,
    );
  }

  // ── Minimised ─────────────────────────────────────────────────────────────
  if (mode === "pill") {
    return createPortal(
      <button
        type="button"
        onClick={() => update({ mode: "open" })}
        className={cn(
          `fixed bottom-5 right-20 ${Z_PANEL} inline-flex h-10 items-center gap-2.5 rounded-pill`,
          "border border-border bg-card px-3.5 text-[13px] font-medium text-foreground",
          "shadow-lg", // design-tokens-ignore: floats over the page, the DS overlay exception
        )}
        data-testid={ids.pillButton(guide.id)}
      >
        <span
          aria-hidden
          className={cn("size-2 rounded-pill", met ? "bg-success" : "bg-warning")}
        />
        <span>
          {guide.title} guide · Step {position} of {total}
        </span>
        <span className="text-fg-muted">Open</span>
      </button>,
      body,
    );
  }

  // ── Finished ──────────────────────────────────────────────────────────────
  if (mode === "closed") {
    return createPortal(
      <div
        role="status"
        className={`fixed bottom-5 right-20 ${Z_PANEL} inline-flex h-10 items-center gap-2.5 rounded-pill bg-primary px-3.5 text-[13px] font-medium text-primary-foreground`}
        data-testid={ids.finishedToast(guide.id)}
      >
        <Check className="size-3.5" aria-hidden />
        {guide.title} guide done.
      </div>,
      body,
    );
  }

  if (!step) return null;
  // A started guide whose steps are not worked out yet. Showing the full list
  // here put a reader on an empty Connections page on "Fix the expired one,
  // step 4 of 5" until the deadline passed.
  if (steps === null && guideStartedAt.current !== null) return null;

  // ── A step ────────────────────────────────────────────────────────────────
  // Hands the work over rather than mentioning it can be handed over. The
  // prompt arrives typed but unsent, so the reader edits it rather than
  // watching something happen they did not ask for. Leaving the page ends this
  // guide; the step is remembered, and Help brings it back here.
  const askAgent = step.agentPrompt ? (
    <Button
      variant="outline"
      size="sm"
      data-testid={ids.askAgentButton(guide.id, step.id)}
      onClick={() => {
        capture("page_guide_handed_off", {
          guide: guide.id,
          step: step.id,
          to: "/agent",
          via: "ask_agent",
        });
        navigate("/agent", { state: { initialDraft: fillPrompt(step) } });
      }}
    >
      Ask the agent
    </Button>
  ) : null;

  return createPortal(
    <>
      {rect && rect.width > 0 && !covered && (
        <div
          aria-hidden
          className={cn(
            `pointer-events-none fixed ${Z_RING} rounded-md`,
            // The design's two-layer highlight: a solid blue edge and a soft
            // halo. `ring-ring` is monochrome in this theme, so it read as a
            // selected pill rather than "look here"; --fs-status-info is the
            // informational blue, which is what a highlight is.
            "[box-shadow:0_0_0_2px_var(--fs-status-info),0_0_0_7px_color-mix(in_srgb,var(--fs-status-info)_25%,transparent)]",
            "motion-safe:animate-pulse",
          )}
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
          }}
          data-testid={ids.ring(guide.id)}
        />
      )}

      <div
        ref={modalRef}
        role="dialog"
        aria-label={`${guide.title} guide, step ${position} of ${total}`}
        className={cn(
          `fixed left-0 top-0 ${Z_PANEL} w-[308px] rounded-lg border border-border bg-card p-4`,
          "shadow-lg", // design-tokens-ignore: the guide floats over the page, the DS overlay exception
          glide &&
            "motion-safe:transition-transform motion-safe:duration-200 [transition-timing-function:var(--fs-motion-ease-out)]",
        )}
        // A transform rather than top/left, so the move animates without
        // re-running layout every frame.
        style={{ transform: `translate(${pos.left}px, ${pos.top}px)` }}
        data-testid={ids.panel(guide.id)}
      >
        {pos.arrow && (
          <span
            aria-hidden
            className="absolute size-3 rotate-45 border-b border-l border-border bg-card"
            style={pos.arrow}
          />
        )}

        <div className="flex items-center justify-between gap-2">
          <span className="text-[12px] font-semibold uppercase tracking-wide text-fg-muted">
            {guide.title} · {state.resurfacing ? "New" : `Step ${position} of ${total}`}
          </span>
          <span className="flex items-center">
            <button
              type="button"
              onClick={() => {
                capture("page_guide_minimised", {
                  guide: guide.id,
                  step: step.id,
                  step_index: index,
                });
                update({ mode: "pill" });
              }}
              aria-label="Minimize the guide"
              title="Minimize"
              className="flex size-7 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-muted hover:text-foreground"
              data-testid={ids.minimizeButton(guide.id)}
            >
              <Minus className="size-3.5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={close}
              aria-label="Close the guide"
              title="Close the guide"
              className="flex size-7 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-muted hover:text-foreground"
              data-testid={ids.skipGuideButton(guide.id)}
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </span>
        </div>

        {/* One bar per step. Clicking a bar goes back to a step already seen —
            forward stays with Next, so nobody skips a waiting step by accident. */}
        <div className="my-2.5 flex gap-1">
          {shown.map((s, n) => {
            // -1: only in the list a `resettle` step will continue into.
            const i = list.indexOf(s);
            const isAhead = i < 0 || i > index;
            const done = !isAhead && i < index;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => (done ? goto(i) : undefined)}
                aria-label={`Step ${n + 1}: ${s.title}`}
                aria-current={i === index ? "step" : undefined}
                disabled={isAhead}
                className={cn(
                  "h-1 flex-1 rounded-sm transition-colors",
                  done && "bg-foreground",
                  i === index && "bg-info",
                  isAhead && "bg-border",
                )}
                data-testid={ids.stepBar(guide.id, s.id)}
              />
            );
          })}
        </div>

        <h2 className="text-[16px] font-semibold text-foreground">{step.title}</h2>
        <p className="mt-1 text-[13px] leading-relaxed text-fg-muted">{step.body}</p>

        {step.wait && !justDone && (
          <p
            className="mt-3 flex items-start gap-2.5 rounded-md [background:color-mix(in_srgb,var(--fs-status-warning)_12%,transparent)] p-2.5 text-[12px] leading-relaxed text-foreground"
            data-testid={ids.waitNote(guide.id, step.id)}
          >
            <span aria-hidden className="mt-1.5 size-2 shrink-0 rounded-pill bg-warning" />
            <span>
              <strong className="font-semibold">Your turn.</strong> {step.wait.instruction}{" "}
              {acted
                ? "Press Done when you have finished."
                : "The guide moves on by itself when you’re done."}
            </span>
          </p>
        )}

        {justDone && (
          <p
            className="mt-3 flex items-center gap-2.5 rounded-md [background:color-mix(in_srgb,var(--fs-status-success)_12%,transparent)] p-2.5 text-[12px] text-foreground"
            data-testid={ids.doneNote(guide.id, step.id)}
          >
            <Check className="size-3.5 shrink-0 text-success" aria-hidden />
            <span>
              <strong className="font-semibold">Done.</strong> On to the next step&hellip;
            </span>
          </p>
        )}

        {!justDone && (
          <div className="mt-4 flex min-h-9 items-center justify-between">
            <span>
              {prev >= 0 && (
                <button
                  type="button"
                  onClick={() => goto(prev)}
                  className="inline-flex h-9 items-center rounded-md border border-border px-2.5 text-[13px] font-medium text-foreground transition-colors hover:bg-muted"
                  data-testid={ids.backButton(guide.id, step.id)}
                >
                  &larr; Back
                </button>
              )}
            </span>
            {step.wait ? (
              <span className="flex items-center gap-2">
                {!acted && askAgent}
                {acted ? (
                  <Button
                    size="sm"
                    onClick={() => pass("confirmed")}
                    data-testid={ids.doneButton(guide.id, step.id)}
                  >
                    Done
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => pass("skipped")}
                    data-testid={ids.skipStepButton(guide.id, step.id)}
                  >
                    Skip this step
                  </Button>
                )}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                {askAgent}
                <Button size="sm" onClick={forward} data-testid={ids.nextButton(guide.id, step.id)}>
                  {last ? (step.lastLabel ?? "Done") : "Next"}
                </Button>
              </span>
            )}
          </div>
        )}
      </div>
    </>,
    body,
  );
}
